import { Button, TextInput } from '@mantine/core';
import { FileWithPath } from '@mantine/dropzone';
import { useForm, zodResolver } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { Api } from '@/shared/api';
import { RouteList } from '@/shared/constants';
import { useDirtyForm } from '@/shared/hooks/use-dirty-form';
import {
  useCurrentProject,
  useProjectPath,
} from '@/shared/projects/project-path';
import { SelectFileInput } from '@/widgets/select-file-input';

import {
  useUpdateCategoryMutation,
  useUploadCategoryPreviewMutation,
} from '../model/edit-category-page.cases';
import { EDIT_CATEGORY_SCHEMA } from '../model/edit-category-page.schemas';
import styles from './edit-category-page.module.scss';

const EditCategoryPage = () => {
  const form = useForm({
    initialValues: {
      title: '',
      previewFile: null as FileWithPath | null,
      previewUrl: '',
    },
    validate: zodResolver(EDIT_CATEGORY_SCHEMA),
  });
  const params = useParams();
  const project = useCurrentProject();
  const withProject = useProjectPath();
  useDirtyForm(form.isDirty());

  const getCategoryQuery = useQuery({
    queryKey: [project, 'category', params.categoryId],
    queryFn: () =>
      Api.categories.getCategory(project, Number(params.categoryId)),
    enabled: !!params.categoryId,
  });
  const updateCategoryMutation = useUpdateCategoryMutation();
  const uploadCategoryPreviewMutation = useUploadCategoryPreviewMutation();
  const navigate = useNavigate();

  async function updateCategory(data: {
    title: string;
    previewFile: FileWithPath | null;
  }) {
    let imageUrl: string | null = null;

    if (data.previewFile) {
      imageUrl = await uploadCategoryPreviewMutation
        .mutateAsync(data.previewFile)
        .catch((reason) => {
          notifications.show({
            message: 'Не удалось загрузить изображение',
            color: 'red',
          });

          throw reason;
        });
    }
    await updateCategoryMutation
      .mutateAsync({
        id: Number(params.categoryId),
        title: data.title,
        imgUrl: imageUrl,
      })
      .then(() => {
        notifications.show({
          message: 'Категория обновлена',
          color: 'green',
        });
        return navigate(
          withProject(RouteList.CATEGORY_DETAILS(String(params.categoryId))),
          {
            replace: true,
          },
        );
      })
      .catch(() => {
        notifications.show({
          message: 'Не удалось обновить категорию',
          color: 'red',
        });
      });
  }

  useEffect(() => {
    if (getCategoryQuery.data) {
      form.setValues({
        title: getCategoryQuery.data.title,
        previewUrl: getCategoryQuery.data.imgUrl,
      });
      form.resetDirty();
    }
  }, [getCategoryQuery.data]);

  return (
    <form className={styles.container} onSubmit={form.onSubmit(updateCategory)}>
      <p className={styles.title}>Редактирование категории</p>
      <TextInput
        placeholder="Название категории"
        label="Название"
        className={styles.nameInput}
        {...form.getInputProps('title')}
      />
      <SelectFileInput
        onDrop={(files) => {
          form.setFieldValue('previewUrl', URL.createObjectURL(files[0]));
          form.setFieldValue('previewFile', files[0]);
        }}
        label="Обложка"
        description={`Допустимые форматы файлов: png
          Размер файла не должен превышать 400 кб`}
        className={styles.previewZone}
        {...form.getInputProps('previewUrl')}
      />
      <img
        className={styles.preview}
        src={form.values.previewUrl}
        alt="preview"
      />
      <div className={styles.actions}>
        <Button type="submit">Сохранить</Button>
        <Button variant="light" onClick={() => history.back()}>
          Отменить
        </Button>
      </div>
    </form>
  );
};

export default EditCategoryPage;
